import React, { PureComponent } from 'react';
import RSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';
import { t } from 'utils/getLiteral';
import Tooltip from 'components/Tooltip';
import ClearIndicator from './ClearIndicator';
import DropdownIndicator from './DropdownIndicator';
import SearchIndicator from './SearchIndicator';
import LoadingIndicator from './LoadingIndicator';
import LockIndicator from './LockIndicator';
import Control from './Control';
import './style.scss';

class Select extends PureComponent {
    constructor(props) {
        super(props);
        this.tooltipRef = React.createRef();
    }

    loadingMessage = () => t('wait_loading');

    onChange = (data, action) => {
        const { onChange, worksWithValue } = this.props;

        if (this.tooltipRef && this.tooltipRef.current) {
            this.tooltipRef.current.resetTooltip();
        }

        let result = data;
        if (worksWithValue) {
            result = data ? data.value : null;
        }
        onChange(result, action);
    };

    renderSelectComponent() {
        const {
            value,
            className,
            onChange,
            mandatory,
            isClearable,
            placeholder,
            hasError,
            components,
            isMulti,
            isDisabled,
            dataTest = '',
            noOptionsMessage,
            options,
            worksWithValue,
            ...props
        } = this.props;

        const finalComponents = {
            ClearIndicator,
            DropdownIndicator,
            LoadingIndicator,
            Control,
            ...components,
        };

        const finalClassName = ['fm-select'];
        if (className) {
            finalClassName.push(className);
        }
        if (hasError) {
            finalClassName.push('input-error');
        }

        // Custom way
        let SelectComponent = RSelect;

        // Async way
        if (this.props.loadOptions) {
            SelectComponent = AsyncSelect;
            finalComponents.DropdownIndicator = SearchIndicator;
            finalComponents.ClearIndicator = null;
            finalClassName.push('fm-select-async');
        }

        // Readonly
        if (isDisabled) {
            finalComponents.DropdownIndicator = LockIndicator;
        }

        const noOptions = t(noOptionsMessage || 'label_no_options');
        const defaultPlaceholder = t('label_selectone');

        let cleanValue = value;
        if (worksWithValue) {
            cleanValue = value !== null ? value.toString() : null;
            cleanValue =
                typeof cleanValue === 'string' && cleanValue.length
                    ? options.find((option) => option.value.toString() === cleanValue)
                    : cleanValue;
        }

        return (
            <SelectComponent
                {...props}
                options={options}
                className={finalClassName.join(' ')}
                classNamePrefix="fm-select"
                onChange={this.onChange}
                mandatory={mandatory || false}
                isClearable={isClearable}
                placeholder={placeholder || defaultPlaceholder}
                components={finalComponents}
                isMulti={isMulti}
                value={cleanValue}
                noOptionsMessage={() => noOptions}
                loadingMessage={this.loadingMessage}
                menuPlacement="auto"
                isDisabled={isDisabled}
                innerProps={{ dataTest }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
        );
    }

    render() {
        const { showTooltip, value } = this.props;

        return showTooltip && value ? (
            <Tooltip title={value.value} position="top" ref={this.tooltipRef}>
                {this.renderSelectComponent()}
            </Tooltip>
        ) : (
            this.renderSelectComponent()
        );
    }
}

Select.propTypes = {
    label: PropTypes.string,
    mandatory: PropTypes.bool,
    className: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    loadOptions: PropTypes.func,
    cacheOptions: PropTypes.bool,
    defaultOptions: PropTypes.bool,
    value: PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.array,
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ]),
    isClearable: PropTypes.bool,
    placeholder: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    error: PropTypes.bool,
    isLoading: PropTypes.bool,
    showTooltip: PropTypes.bool,
    isDisabled: PropTypes.bool,
    dataTest: PropTypes.string,
    noOptionsMessage: PropTypes.string,
    worksWithValue: PropTypes.bool,
};

export default Select;
